import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Heading,
  useToast,
  Textarea,
  SimpleGrid,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import ApiResponse from "../../../services/ApiResponse";
import { getDecodedToken } from "../../../services/Token";
import {
  Carrera,
  CarreraCreate,
  Modalidad,
  Turno,
  Tipo
} from "../../../services/models/Carrera";
import handleApiError from "../../../utils/ApiErrorHandler";
import { ApiValidationResponse } from "../../../services/models/ApiValidationResponse";

const getInitialDate = (date?: string) => (date ? date.split("T")[0] : "");

const formatToSqlDateTime = (dateStr: string | undefined) => {
  if (!dateStr || dateStr === "") return "";
  const [year, month, day] = dateStr.split("-");
  return `${year}-${month}-${day} 00:00:00`;
};

const CarreraAddPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const decoded = getDecodedToken();
  const location = useLocation();
  const carreraToEdit = location.state ? (location.state as Carrera) : null;

  const nombreRef = useRef<HTMLInputElement>(null);
  const descripcionRef = useRef<HTMLInputElement>(null);
  const planDeEstudioRef = useRef<HTMLTextAreaElement>(null);
  const cupoRef = useRef<HTMLInputElement>(null);
  const duracionAniosRef = useRef<HTMLInputElement>(null);
  const duracionMesesRef = useRef<HTMLInputElement>(null);
  const fechaInscripcionRef = useRef<HTMLInputElement>(null);
  const fechaFinalizacionInscripcionRef = useRef<HTMLInputElement>(null);
  const fechaInicioDeCursadoRef = useRef<HTMLInputElement>(null);
  const inscripcionRef = useRef<HTMLInputElement>(null);
  const prioridadRef = useRef<HTMLSelectElement>(null);

  const [modalidad, setModalidad] = useState<Modalidad>(carreraToEdit?.modalidad as Modalidad || Modalidad.Presencial);
  const [tipo, setTipo] = useState<Tipo>(carreraToEdit?.tipo as Tipo || Tipo.Tecnicatura);
  const [turno, setTurno] = useState<Turno>(carreraToEdit?.turno as Turno || Turno.Mañana);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const duracionAnios = parseInt(duracionAniosRef.current?.value || "0");
    const duracionMeses = parseInt(duracionMesesRef.current?.value || "0");

    if (duracionAnios === 0 && duracionMeses === 0) {
      toast({
        title: 'Error de validación',
        description: 'Debe completar la duración en años o meses.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const formData: CarreraCreate = {
      nombre: nombreRef.current?.value || "",
      tipo: tipo,
      turno: turno,
      descripcion: descripcionRef.current?.value || undefined,
      plan_de_estudio: planDeEstudioRef.current?.value || undefined,
      modalidad: modalidad,
      cupo: cupoRef.current?.value ? parseInt(cupoRef.current.value) : undefined,
      duracion_anios: duracionAnios,
      duracion_meses: duracionMeses,
      fecha_inscripcion: formatToSqlDateTime(fechaInscripcionRef.current?.value),
      fecha_finalizacion_inscripcion: formatToSqlDateTime(fechaFinalizacionInscripcionRef.current?.value),
      fecha_inicio_cursado: formatToSqlDateTime(fechaInicioDeCursadoRef.current?.value),
      inscripcion: inscripcionRef.current?.value || undefined,
      institucion_id: decoded?.institucion_id || 0,
      prioridad: prioridadRef.current?.value ? parseInt(prioridadRef.current.value) : 0,
    };

    const apiResponse = new ApiResponse<Carrera>();
    
    if (carreraToEdit) {
      await apiResponse.useFetch(`carreras/${carreraToEdit.id}`, "PATCH", formData);
    } else {
      await apiResponse.useFetch("carreras", "POST", formData);
    }

    if (!apiResponse.error) {
      toast({
        title: carreraToEdit ? "Carrera actualizada" : "Carrera creada",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      navigate("/dashboard/carreras");
    } else {
      if (typeof apiResponse.error === "object" && apiResponse.error !== null && "errors" in apiResponse.error) {
        handleApiError(apiResponse.error as ApiValidationResponse, toast);
      } else {
        toast({
          title: "Error",
          description: String(apiResponse.error) || "Error en el servidor",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Box maxW="2xl" mx="auto" mt={8} p={8} borderWidth={1} borderRadius="xl" boxShadow="lg" bg="white">
      <Heading mb={8} textAlign="center" size="lg" color="teal.600">
        {carreraToEdit ? "Editar Carrera" : "Registrar Nueva Carrera"}
      </Heading>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6}>
          <FormControl id="nombre" isRequired>
            <FormLabel fontWeight="bold">Nombre</FormLabel>
            <Input ref={nombreRef} defaultValue={carreraToEdit?.nombre || ""} />
          </FormControl>

          <SimpleGrid columns={[1, 2]} spacing={4} width="full">
            <FormControl id="tipo" isRequired>
              <FormLabel fontWeight="bold">Tipo</FormLabel>
              <Select value={tipo} onChange={(e) => setTipo(e.target.value as Tipo)}>
                {Object.values(Tipo).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl id="modalidad" isRequired>
              <FormLabel fontWeight="bold">Modalidad</FormLabel>
              <Select value={modalidad} onChange={(e) => setModalidad(e.target.value as Modalidad)}>
                {Object.values(Modalidad).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>

          <FormControl id="descripcion">
            <FormLabel fontWeight="bold">Descripción</FormLabel>
            <Input ref={descripcionRef} defaultValue={carreraToEdit?.descripcion || ""} />
          </FormControl>

          <FormControl id="plan_de_estudio">
            <FormLabel fontWeight="bold">Plan de Estudio</FormLabel>
            <Textarea ref={planDeEstudioRef} defaultValue={carreraToEdit?.plan_de_estudio || ""} rows={5} />
          </FormControl>

          <SimpleGrid columns={[1, 3]} spacing={4} width="full">
            <FormControl id="duracion_anios">
              <FormLabel fontWeight="bold">Años</FormLabel>
              <Input type="number" ref={duracionAniosRef} defaultValue={carreraToEdit?.duracion_anios || "0"} />
            </FormControl>
            <FormControl id="duracion_meses">
              <FormLabel fontWeight="bold">Meses</FormLabel>
              <Input type="number" ref={duracionMesesRef} defaultValue={carreraToEdit?.duracion_meses || "0"} />
            </FormControl>
            <FormControl id="cupo">
              <FormLabel fontWeight="bold">Cupo</FormLabel>
              <Input ref={cupoRef} type="number" min={0} defaultValue={carreraToEdit?.cupo || ""} />
            </FormControl>
          </SimpleGrid>

          <Box width="full" p={4} borderRadius="md" bg="gray.50">
            <SimpleGrid columns={[1, 2]} spacing={4}>
              <FormControl id="fecha_inscripcion">
                <FormLabel fontWeight="bold">Inicio Inscripción</FormLabel>
                <Input type="date" ref={fechaInscripcionRef} defaultValue={getInitialDate(carreraToEdit?.fecha_inscripcion)} />
              </FormControl>
              <FormControl id="fecha_finalizacion_inscripcion">
                <FormLabel fontWeight="bold">Cierre Inscripción</FormLabel>
                <Input type="date" ref={fechaFinalizacionInscripcionRef} defaultValue={getInitialDate(carreraToEdit?.fecha_finalizacion_inscripcion)} />
              </FormControl>
              <FormControl id="fecha_inicio_cursado">
                <FormLabel fontWeight="bold">Inicio Cursado</FormLabel>
                <Input type="date" ref={fechaInicioDeCursadoRef} defaultValue={getInitialDate(carreraToEdit?.fecha_inicio_cursado)} />
              </FormControl>
              <FormControl id="turno" isRequired>
                <FormLabel fontWeight="bold">Turno</FormLabel>
                <Select value={turno} onChange={(e) => setTurno(e.target.value as Turno)}>
                  {Object.values(Turno).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
          </Box>

          <FormControl id="inscripcion">
            <FormLabel fontWeight="bold">Link de Inscripción</FormLabel>
            <Input ref={inscripcionRef} defaultValue={carreraToEdit?.inscripcion || ""} />
          </FormControl>

          <VStack width="full" spacing={3} pt={4}>
            <Button type="submit" colorScheme="teal" size="lg" width="full">
              {carreraToEdit ? "Guardar Cambios" : "Crear Carrera"}
            </Button>
            <Button type="button" onClick={() => navigate("/dashboard/carreras")} variant="ghost" width="full">
              Cancelar
            </Button>
          </VStack>
        </VStack>
      </form>
    </Box>
  );
};

export default CarreraAddPage;